import React, { useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Modal,
  Paper,
  Select,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { DOCUMENT_CATEGORIES, StratifyDocument } from "@stratify/shared";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsCategoryUpdating,
  updateCategoryStart,
} from "../../../store/documentsSlice";
import { trackEvent } from "../../../../analytics/store/analyticsSlice";

interface DocumentCategoryCardProps {
  document: StratifyDocument;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 1,
};

export const DocumentCategoryCard: React.FC<DocumentCategoryCardProps> = ({
  document,
}) => {
  const dispatch = useDispatch();
  const isUpdating = useSelector(selectIsCategoryUpdating);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(
    document.metadata.category?.id || ""
  );
  const [rerunInsights, setRerunInsights] = useState(false);

  const handleUpdate = () => {
    dispatch(
      updateCategoryStart({
        documentId: document.id,
        categoryId: selectedCategory,
        rerunInsights,
      })
    );
    dispatch(
      trackEvent({
        name: "document_category_changed",
        params: {
          document_id: document.id,
          old_category: document.metadata.category?.id || "none",
          new_category: selectedCategory,
          rerun_insights: rerunInsights,
        },
      })
    );
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setSelectedCategory(document.metadata.category?.id || "");
    setRerunInsights(false);
    setIsModalOpen(false);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Box>
            <Typography variant="h6" gutterBottom>
              Category
            </Typography>
          </Box>
          <Typography data-testid="document-category-name">
            {document.metadata.category?.name || "None"}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {document.metadata.category?.description || "None"}
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<EditIcon />}
          onClick={() => setIsModalOpen(true)}
          disabled={isUpdating}
          sx={{ minWidth: 0, p: 1 }}
        >
          Edit
        </Button>
      </Box>

      <Modal
        open={isModalOpen}
        onClose={handleCancel}
        aria-labelledby="update-category-modal"
      >
        <Box sx={modalStyle}>
          <Typography variant="h6" component="h2" gutterBottom>
            Update Category
          </Typography>

          <Stack spacing={3}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Select Category
              </Typography>
              <Select
                fullWidth
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                disabled={isUpdating}
                size="small"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {Object.entries(DOCUMENT_CATEGORIES).map(([id, category]) => (
                  <MenuItem key={id} value={id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={rerunInsights}
                  onChange={(e) => setRerunInsights(e.target.checked)}
                  disabled={isUpdating}
                />
              }
              label="Re-run insights analysis"
            />

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button onClick={handleCancel} disabled={isUpdating}>
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                variant="contained"
                disabled={isUpdating}
                startIcon={isUpdating && <CircularProgress size={20} />}
              >
                Update
              </Button>
            </Box>
          </Stack>
        </Box>
      </Modal>
    </Paper>
  );
};
